import React, { useEffect, useState } from 'react';
import { Stack, Checkbox, Paper, Grid, TextField, Button, Typography, FormControlLabel, FormControl, InputLabel, MenuItem, Select, FormLabel, Radio, RadioGroup } from '@mui/material/';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

//components imports
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import { GenericFormObject } from '../dataObjects/GenericFormObject';

interface District {
    code: number;
    description: string;
}

interface FormDataProps {
    formData: GenericFormObject;
    closeGenericForm: () => void;
}

const ChangePurposeForm = (props: FormDataProps): JSX.Element => {

    
    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();

    /*Initial State*/
    const [form, setForm] = useState<GenericFormObject>(props.formData);
    const [officeType, setOfficeType] = useState<string>("");
    const [districts, setDistricts] = useState<District[]>([]);
    const [districtId, setDistrictId] = useState<number>(-1);
    const [create200COPLetter, setCreate200COPLetter] = useState<boolean>(true);

    useEffect(() => {
        setForm(props.formData);
    }, [props.formData]);

    useEffect(() => {
        if (props.formData.officeSoughtDistrictCode)
            resetOfficeType(props.formData.officeSoughtDistrictCode)
    }, [props.formData]);

    useEffect(() => {
        fetch(`/Reference/GetActiveDistrictsByOfficeGroup?officeTypeGroupId=${officeType}`)
            .then(res => res.json())
            .then(data => {
                let districtList = data;
                setDistricts(districtList);
                setDistrictId(-1);
            });
    }, [officeType]);

    //after getting the district list, populate the district id from the form
    useEffect(() => {
        if (props.formData.officeSoughtDistrictCode && districts.length > 0) {
            const match = districts.find((d: District) => d.code === Number(form.officeSoughtDistrictCode));
            if (match) {
                setDistrictId(match.code);
            }
        }
    }, [districts, props.formData.officeSoughtDistrictCode]);

    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
        if (alertInfo?.type === 'success') {
            props.closeGenericForm();
        }
    }

    const saveOfficeSoughtInfo = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        formData.append("cpfId", form.cpfID);
        formData.append("Create200CPLetter", create200COPLetter.toString());
        formData.append("url", form.formIntakeBlobUrl);

        fetch(`/LLMFormProcessor/UpdateOfficeSought`,
            {
                method: 'POST',
                body: formData,
            })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: UpdateOfficeSought',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'The New Office Sought Saved',
                        errorMessage: 'The 101P form was posted.\nThe new office sought was saved.\nThe draft archer letters were created in Archer.',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    //open profiles??
                }
            });
    }

    const resetOfficeType = (districtCode: string) => {

        if (!districtCode) return;

        let code = Number(districtCode);

        if (code <= 199) {
            // senate
            setOfficeType('2');
        } else if (code <= 399) {
            // house
            setOfficeType('1');
        } else if (code >= 500 && code <= 599) {
            // Sheriff
            setOfficeType('6');
        }else if (code >= 600 && code <= 699) {
            // District Attorney
            setOfficeType('5');
        } else if (code >= 1101 && code <= 1108) {
            // Governor's Council
            setOfficeType('4');
        } else if (code >= 1109 && code <= 1114) {
            // Statewide
            setOfficeType('3');
        } else if (code >= 4000 && code <= 4999) {
            // Mayoral
            setOfficeType('8');
        } else if (code >= 5000 && code <= 5999) {
            // City Councilor
            setOfficeType('9');
        } else {
            setOfficeType('10');
        }
    }

    return (
        <>
            <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => saveOfficeSoughtInfo(event)}>
                <Paper sx={{ p: 3 }} >
                    <Grid item xs={12} marginTop={2} >
                        <Stack spacing={2}>
                            <Typography variant="subtitle1" fontWeight={550}>P101 Form Info</Typography>
                            <FormControl>
                                <FormLabel>Reason:</FormLabel>
                                <RadioGroup
                                    defaultValue="0"
                                    name="IsAdminChange"
                                >
                                    <FormControlLabel value="0" control={<Radio />} label="Change of Purpose " />
                                    <FormControlLabel value="1" control={<Radio />} label="Administrative Change" />
                                </RadioGroup>
                            </FormControl>
                            <TextField
                                margin="normal"
                                label="Please specify the reason for change"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                name="reason"
                                defaultValue="Change of Purpose Form Submitted"
                                required
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    sx={{ minWidth: 100, width: 350 }}
                                    name="effectiveDate"
                                    label="Effective Date"
                                    defaultValue={dayjs()}
                                />
                            </LocalizationProvider>
                            <TextField
                                margin="normal"
                                label="Current Office Sought (from the form, for reference)"
                                size="small"
                                defaultValue={form.officePreviouslyHeldOrSought}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                            <TextField
                                margin="normal"
                                label="New Office Sought (from the form, for reference)"
                                size="small"
                                name="newOfficeSoughtDescription"
                                defaultValue={form.officeSought}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                            <Stack direction="row" spacing={1} >
                                <FormControl sx={{ m: 1, width: 200 }} size="small">
                                    <InputLabel id="office-select-label">Select an office</InputLabel>
                                    <Select
                                        labelId="office-select-label"
                                        id="newOfficeCodeSought"
                                        label="Select an office"
                                        value={officeType}
                                        onChange={(event) => setOfficeType(event.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Select an office:</em>
                                        </MenuItem>
                                        <MenuItem value="1">House</MenuItem>
                                        <MenuItem value="2">Senate</MenuItem>
                                        <MenuItem value="3">Statewide</MenuItem>
                                        <MenuItem value="4">Governor's Council</MenuItem>
                                        <MenuItem value="5">District Attorney</MenuItem>
                                        <MenuItem value="6">Sheriff</MenuItem>
                                        <MenuItem value="8">Mayoral (Depository)</MenuItem>
                                        <MenuItem value="9">City Councilor</MenuItem>
                                        <MenuItem value="10">Other Depository</MenuItem>
                                        <MenuItem value="11">Local (R7)</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                    <InputLabel id="district-select-label">Select a District</InputLabel>
                                    <Select
                                        label="Select a District"
                                        value={districtId}
                                        name="newDistrictCodeSought"
                                        onChange={(event) => setDistrictId(Number(event.target.value))}
                                    >
                                        <MenuItem value={-1}>Select a District</MenuItem>
                                        {
                                            districts && districts.map((district) => (
                                                <MenuItem key={district.code} value={district.code}>{district.description} ({district.code})</MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Stack>
                            <FormControlLabel
                                control={<Checkbox checked={create200COPLetter} color="primary" onChange={(e) => setCreate200COPLetter(e.target.checked)} />}
                                label={`Create a 200COP Change of Purpose Acknowledgement Letter?`}
                            />
                        </Stack>
                    </Grid>
                </Paper>
                <Grid item xs={12} marginTop={3} marginBottom={3} >
                    <Stack direction="row" spacing={2} justifyContent={"flex-end"} marginTop={2} >
                        <Button variant="contained" color="warning" onClick={props.closeGenericForm}>Cancel</Button>
                        <Button variant="contained" type="submit">Save</Button>
                    </Stack>
                </Grid>
            </form>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )
}

export default ChangePurposeForm;